'use client';
import { useEffect, useRef, useState } from 'react';

interface StatItem {
  value: number;
  suffix: string;
  prefix: string;
  label: string;
  target: number;
}

const stats: StatItem[] = [
  { value: 0, suffix: '', prefix: '', label: 'Premium Collections', target: 43 },
  { value: 0, suffix: '+', prefix: '', label: 'Canva/KDP Templates', target: 14700 },
  { value: 0, suffix: '+', prefix: '', label: 'Stock Photos', target: 100000 },
  { value: 0, suffix: '', prefix: '', label: 'HTML5 Games', target: 153 },
  { value: 0, suffix: '', prefix: '', label: 'UI Kits', target: 145 },
  { value: 0, suffix: '', prefix: '$', label: 'One-Time Price', target: 49 },
];

function formatNumber(n: number, target: number): string {
  if (target >= 100000) return (Math.floor(n / 1000)).toString() + 'K';
  if (target >= 10000) return (Math.floor(n / 100) * 100).toLocaleString();
  return Math.floor(n).toString();
}

export default function StatsSection() {
  const [counts, setCounts] = useState(stats.map(() => 0));
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const duration = 2000;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCounts(stats.map((s) => s.target * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started]);

  return (
    <section className="relative py-12 overflow-hidden" aria-label="Bundle statistics">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(124,58,237,0.4), transparent)' }} aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-2xl overflow-hidden" ref={ref}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y lg:divide-y-0" style={{ borderColor: 'rgba(30,41,59,0.4)' }}>
            {stats.map((stat, i) => (
              <div key={i} className="stat-item flex flex-col items-center gap-1">
                <div className="stat-number">
                  {stat.prefix}{formatNumber(counts[i], stat.target)}{stat.suffix}
                </div>
                <div className="text-sm font-medium text-center" style={{ color: 'var(--muted-foreground)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(124,58,237,0.4), transparent)' }} aria-hidden="true" />
    </section>
  );
}
